import { useEffect, useRef } from "react";
import { io, type Socket } from "socket.io-client";

import { tokenStore } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";

let _socket: Socket | null = null;
const _listeners = new Map<string, Set<(payload: unknown) => void>>();

function ensureSocket(): Socket | null {
  if (_socket) return _socket;
  const tokens = tokenStore.read();
  if (!tokens) return null;
  // Same-origin connection so the Vite proxy upgrades to WS.
  _socket = io({
    path: "/socket.io",
    transports: ["websocket", "polling"],
    auth: { token: tokens.accessToken },
    reconnection: true,
    reconnectionDelay: 1000,
  });
  _socket.onAny((event, payload) => {
    const set = _listeners.get(event);
    if (!set) return;
    for (const fn of set) {
      try {
        fn(payload);
      } catch {
        /* swallow */
      }
    }
  });
  return _socket;
}

export function disconnectSocket(): void {
  if (_socket) {
    _socket.disconnect();
    _socket = null;
  }
}

/** Subscribe to a Socket.IO event for the lifetime of the component. */
export function useRealtime<T = unknown>(
  event: string,
  handler: (payload: T) => void
): void {
  const { user } = useAuth();
  const ref = useRef(handler);
  ref.current = handler;

  useEffect(() => {
    if (!user) return undefined;
    const sock = ensureSocket();
    if (!sock) return undefined;
    if (!_listeners.has(event)) _listeners.set(event, new Set());
    const set = _listeners.get(event)!;
    const wrapped = (payload: unknown) => ref.current(payload as T);
    set.add(wrapped);
    return () => {
      set.delete(wrapped);
    };
  }, [event, user]);
}


/**
 * Imperative access to the singleton socket. Used by pages that need
 * to ``emit`` (e.g. ``unit.join`` / ``unit.leave``) in addition to
 * subscribing via :func:`useRealtime`.
 */
export function useSocket(): { socket: Socket | null } {
  const { user } = useAuth();
  if (!user) return { socket: null };
  return { socket: ensureSocket() };
}
